import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useProfileStore } from '@/store/profileStore';
import { useAuthStore } from '@/store/authStore';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/store/language-store';
import { useEffect, useState } from 'react';
import { supabase } from '@flows.cash/core';
import { queryClient } from '@/lib/queryClient';
import i18n from '@/i18n';

export interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
  region: string
}

interface UserPreferences {
  notifications?: boolean;
  theme?: string;
  currency?: string;
  language?: string;
}

interface ProfileSettings {
  preferences?: UserPreferences;
}

interface ProfileStore {
  settings: ProfileSettings | null;
  updateSettings: (userId: string, data: Partial<ProfileSettings>) => Promise<void>;
  fetchSettings: (userId: string) => Promise<void>;
  refreshUserProfile: (userId: string) => Promise<void>;
}

interface AuthUser {
  id: string;
  uid?: string;
}

export const languages: Language[] = [
  { 
    code: 'en', 
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    region: 'United States'
  },
  { 
    code: 'tr', 
    name: 'Turkish',
    nativeName: 'Türkçe',
    flag: '🇹🇷',
    region: 'Turkey'
  },
  { 
    code: 'ar', 
    name: 'Arabic',
    nativeName: 'العربية',
    flag: '🇸🇦',
    region: 'Saudi Arabia'
  },
  { 
    code: 'es', 
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    region: 'Spain'
  },
  { 
    code: 'fr', 
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
    region: 'France'
  },
  { 
    code: 'de', 
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    region: 'Germany'
  },
];

export default function LanguageSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore() as { user: AuthUser | null };
  const { settings, updateSettings, fetchSettings, refreshUserProfile } = useProfileStore() as ProfileStore;
  const { language, setLanguage } = useLanguage();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);

  // Check the actual language preference from the database directly
  useEffect(() => {
    const getDbLanguage = async () => {
      if (!user?.id) return;
      
      try {
        // Get language directly from user_preferences table
        const { data, error } = await supabase
          .from('user_preferences')
          .select('language')
          .eq('id', user.id)
          .single();
          
        if (error) {
          console.error('Error fetching language from user_preferences:', error);
          return;
        }
        
        if (data?.language) {
          console.log('Retrieved language from user_preferences in settings view:', data.language);
          setCurrentLanguage(data.language);
        }
      } catch (err) {
        console.error('Error getting language preference from DB:', err);
      }
    };
    
    getDbLanguage();
  }, [user?.id]);

  // Check for direct DB language on mount
  useEffect(() => {
    if (user?.id) {
      // Directly use our i18n sync function
      i18n.syncFromDatabase(user.id).then(lang => {
        if (lang) {
          console.log(`LanguageSettings: Synchronized to language ${lang} from database`);
          setCurrentLanguage(lang);
        }
      });
    }
  }, [user?.id]);

  // Log on every render to help debug
  useEffect(() => {
    console.log('LanguageSettings component mounted');
    console.log('Current i18n language:', i18n.language);
    console.log('Current language store language:', language);
    console.log('Current profile settings language:', settings?.preferences?.language);
    console.log('Current component state language:', currentLanguage);
    console.log('User object:', user);
    
    // If user is logged in but we don't have settings, fetch them
    if (user?.id && !settings) {
      console.log('Fetching user settings on mount');
      fetchSettings(user.id);
    }
  }, [i18n.language, language, settings?.preferences?.language, user, fetchSettings, currentLanguage]);

  // Update currentLanguage whenever any of the sources change
  useEffect(() => {
    // Priority: Database > Profile Settings > i18n > Language Store
    const dbLanguage = settings?.preferences?.language;
    const i18nLang = i18n.language;
    const storeLang = language;
    
    if (dbLanguage) {
      setCurrentLanguage(dbLanguage);
    } else if (i18nLang) {
      setCurrentLanguage(i18nLang);
    } else if (storeLang) {
      setCurrentLanguage(storeLang);
    }
  }, [settings?.preferences?.language, i18n.language, language]);

  const handleLanguageChange = async (languageCode: string) => {
    console.log('Language change requested:', languageCode);
    console.log('User object:', user);
    
    // Get the user ID from the user object (could be in .id or .uid depending on implementation)
    const userId = user?.id;
    
    if (!userId) {
      console.error('Cannot update language: No user ID available');
      toast.error('Cannot update language settings. Please sign in again.');
      return;
    }

    try {
      // Update our local state immediately for a responsive UI
      setCurrentLanguage(languageCode);
      
      // Clear all caches first
      console.log('Invalidating all caches before language change');
      queryClient.invalidateQueries();
      
      // 1. Directly update the user_preferences table with the new language
      console.log('Directly updating user_preferences table with language:', languageCode);
      const { error: preferencesError } = await supabase
        .from('user_preferences')
        .upsert({
          id: userId,
          user_id: userId,
          language: languageCode,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });
      
      if (preferencesError) {
        console.error('Error updating user_preferences:', preferencesError);
        throw preferencesError;
      }
      
      // 2. Update user settings in profile store
      console.log('Updating user settings with language:', languageCode, 'for user ID:', userId);
      await updateSettings(userId, {
        preferences: {
          ...settings?.preferences,
          language: languageCode,
        },
      });
      
      // 3. Update the language store
      console.log('Updating language store with:', languageCode);
      setLanguage(languageCode);
      
      // 4. Update i18n instance and localStorage
      console.log('Changing i18n language to:', languageCode);
      await i18n.changeLanguage(languageCode);
      
      // 5. Update localStorage manually to ensure it's set
      console.log('Updating localStorage with language:', languageCode);
      localStorage.setItem('i18nextLng', languageCode);
      
      // 6. Update RTL/LTR direction if needed
      document.documentElement.dir = languageCode === 'ar' ? 'rtl' : 'ltr';
      
      // 7. Refresh user profile to ensure new settings are loaded
      console.log('Refreshing user profile after language change');
      await refreshUserProfile(userId);
      
      // 8. Re-fetch settings to ensure we have the latest data
      await fetchSettings(userId);

      console.log('Language successfully updated to:', languageCode);
      toast.success(t('settings.language.updateSuccess', 'Language updated successfully'));
      
      // 9. Force a page reload to ensure all systems are in sync
      console.log('Reloading page to ensure all language settings are in sync');
      
      // Special handling for Turkish language which has known issues with translation cache
      if (languageCode === 'tr') {
        console.log('Turkish language selected, applying special reload sequence');
        // First clear all caches
        localStorage.removeItem('i18nextLng');
        localStorage.setItem('i18nextLng', languageCode);
        
        // Force full page reload (not SPA navigation)
        window.location.href = window.location.href.split('?')[0]; // Remove any query params
        return; // Exit early
      }
      
      window.location.reload();
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error(t('settings.language.updateError', 'Failed to update language'));
      // Reset the local state in case of error
      setCurrentLanguage(i18n.language);
    }
  };

  return (
    <Card className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm p-4">
      <CardHeader className="px-0">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-primary/10">
            <Globe className="h-5 w-5 text-primary dark:text-primary-dark" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
              {t('settings.language.title', 'Language Settings')}
            </CardTitle>
            <CardDescription className="text-text-secondary dark:text-text-dark-secondary">
              {t('settings.language.description', 'Choose your preferred language for the application')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-0">
        <div className="mb-4 text-sm text-text-secondary dark:text-text-dark-secondary">
          Current language: {currentLanguage} ({languages.find(l => l.code === currentLanguage)?.name || 'Unknown'})
        </div>
        <RadioGroup
          value={currentLanguage}
          onValueChange={handleLanguageChange}
          className="space-y-2"
        >
          {languages.map((language) => {
            const isSelected = currentLanguage === language.code;
            return (
              <div
                key={language.code}
                className={cn(
                  "relative flex items-center rounded-xl border border-border dark:border-border-dark p-4 transition-colors",
                  isSelected && "border-primary dark:border-primary-dark bg-primary/5 dark:bg-primary-dark/10",
                  !isSelected && "hover:border-primary/50 dark:hover:border-primary-dark/50 hover:bg-primary/5 dark:hover:bg-primary-dark/5"
                )}
              >
                <RadioGroupItem
                  value={language.code}
                  id={language.code}
                  className="border-border dark:border-border-dark data-[state=checked]:border-primary dark:data-[state=checked]:border-primary-dark data-[state=checked]:text-primary dark:data-[state=checked]:text-primary-dark"
                />
                <Label
                  htmlFor={language.code}
                  className="flex flex-1 cursor-pointer select-none pl-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xl" role="img" aria-label={`${language.name} flag`}>
                        {language.flag}
                      </span>
                      <div>
                        <p className="font-medium text-text-primary dark:text-text-dark-primary">
                          {language.name}
                        </p>
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                          {language.nativeName} • {language.region}
                        </p>
                      </div>
                    </div>
                  </div>
                </Label>
                {isSelected && (
                  <div className="absolute right-4 text-xs font-medium text-primary dark:text-primary-dark bg-primary/10 dark:bg-primary-dark/20 px-2 py-0.5 rounded-full">
                    {t('settings.language.current', 'Current')}
                  </div>
                )}
              </div>
            );
          })}
        </RadioGroup>
      </CardContent>
    </Card>
  );
} 